import { createClient } from '@supabase/supabase-js';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced environment variable validation
if (!supabaseUrl) {
  console.error('Missing VITE_SUPABASE_URL environment variable');
  toast.error('Missing Supabase URL configuration');
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  console.error('Missing VITE_SUPABASE_ANON_KEY environment variable');
  toast.error('Missing Supabase key configuration');
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

console.log('Initializing Supabase client with URL:', supabaseUrl);

// Create Supabase client with enhanced configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
    flowType: 'pkce',
    debug: false,
    storageKey: `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`,
  },
  realtime: {
    params: {
      eventsPerSecond: 1
    }
  },
  db: {
    schema: 'public'
  },
  global: {
    fetch: (url, options = {}) => {
      // Add custom fetch logic here, e.g., timeout handling
      return fetch(url, {
        ...options,
        signal: AbortSignal.timeout(30000) // 30 second timeout
      });
    }
  }
});

// Test the connection when the app starts
async function testConnection() {
  try {
    const start = Date.now();
    console.log('Testing Supabase connection...');
    
    // Try a simple auth check instead of table access
    const { data: session } = await supabase.auth.getSession();
    
    if (session) {
      const time = Date.now() - start;
      console.log(`Supabase connection successful (${time}ms)`);
      return true;
    }
    
    // If no session, try a minimal query
    try {
      // Try to query public schema existence (minimal permissions required)
      const { data, error } = await supabase
        .rpc('get_schema_version', { schema_name: 'public' })
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        // Not critical, just log warning
        console.warn('Schema version check failed:', error);
      }
      
      const time = Date.now() - start;
      console.log(`Supabase connection partially successful (${time}ms)`);
      return true;
    } catch (innerErr) {
      console.warn('Minimal query failed but continuing anyway:', innerErr);
      // We'll continue anyway as this is not critical
      return true;
    }
  } catch (err) {
    console.error('Connection test error:', err);
    toast.error('Database connection issue - some features may not work');
    // Return true anyway to allow app to load
    return true;
  }
}

// Run connection test with a slight delay to not block app initialization
setTimeout(testConnection, 2000);

// Debounced auth state change handler
const debouncedAuthChange = debounce((event: string, session: any) => {
  console.log('Supabase Auth State Change:', { 
    event, 
    hasSession: !!session,
    userId: session?.user?.id,
    userEmail: session?.user?.email,
    sessionStatus: session ? 'active' : 'none'
  });
}, 1000);

// Disable auth state debug logging
supabase.auth.onAuthStateChange((event, session) => {
  // Completely disable auth state logging in supabase.ts
  // This will prevent the console from being flooded with auth logs
  return;
});

// Keep alive connection with reduced frequency
let lastActivity = Date.now();
const TIMEOUT = 1000 * 60 * 30; // Increase to 30 minutes to reduce activity

const keepAlive = debounce(() => {
  const now = Date.now();
  if (now - lastActivity > TIMEOUT) {
    supabase.auth.getSession().then(({ data: { session }}) => {
      if (session) {
        supabase.from('profiles').select('count').single().then(({ error }) => {
          if (error) {
            console.warn('Keep-alive request failed:', error);
          } else {
            console.log('Keep-alive successful');
          }
        });
      }
    });
  }
  lastActivity = now;
}, 5000);

// Schedule periodic keep-alive
const keepAliveInterval = setInterval(keepAlive, TIMEOUT);

// Clean up interval on window unload
window.addEventListener('beforeunload', () => {
  clearInterval(keepAliveInterval);
});

// Update lastActivity on user interaction with debounce
const updateActivity = debounce(() => {
  lastActivity = Date.now();
}, 1000);

document.addEventListener('mousemove', updateActivity);
document.addEventListener('keypress', updateActivity);

// Export a function to delete domains directly with error handling
export const deleteDomain = async (domainId: string) => {
  console.log('⚠️ DIRECT DELETE: Attempting to delete domain with ID:', domainId);
  
  // Method 1: Try the standard delete first
  try {
    const { error } = await supabase
      .from('approved_domains')
      .delete()
      .eq('id', domainId);
      
    if (error) {
      console.error('⚠️ DIRECT DELETE: Standard method failed:', error);
      throw error;
    }
    
    console.log('✅ DIRECT DELETE: Domain deleted successfully with standard method');
    return { success: true, method: 'standard' };
  } catch (error) {
    console.error('⚠️ DIRECT DELETE: Error with standard delete:', error);
    
    // Method 2: Try using an RPC function if available
    try {
      const { error: rpcError } = await supabase
        .rpc('delete_domain', { domain_id: domainId });
        
      if (rpcError) {
        console.error('⚠️ DIRECT DELETE: RPC method failed:', rpcError);
        throw rpcError;
      }
      
      console.log('✅ DIRECT DELETE: Domain deleted successfully with RPC method');
      return { success: true, method: 'rpc' };
    } catch (rpcFallbackError) {
      console.error('⚠️ DIRECT DELETE: Error with RPC delete fallback:', rpcFallbackError);
      
      // Method 3: Final fallback - use raw SQL (if function exists)
      try {
        const { error: sqlError } = await supabase
          .rpc('execute_sql', {
            query_text: `DELETE FROM approved_domains WHERE id = '${domainId}'`
          });
          
        if (sqlError) {
          console.error('⚠️ DIRECT DELETE: SQL method failed:', sqlError);
          throw sqlError;
        }
        
        console.log('✅ DIRECT DELETE: Domain deleted successfully with SQL method');
        return { success: true, method: 'sql' };
      } catch (sqlFallbackError) {
        console.error('⚠️ DIRECT DELETE: All deletion methods failed');
        throw sqlFallbackError;
      }
    }
  }
};

// Export a function to test connection on demand
export const testSupabaseConnection = testConnection;

// Function to diagnose database structure and permissions
export const diagnoseDatabaseStructure = async () => {
  const results: Record<string, any> = {
    tables: {},
    permissions: {},
    test_operations: {}
  };
  
  console.log('🔍 DIAGNOSTICS: Starting database structure diagnosis');
  
  try {
    // 1. Check if approved_domains table exists
    console.log('🔍 DIAGNOSTICS: Checking if approved_domains table exists');
    const { data: tables, error: tablesError } = await supabase
      .from('information_schema.tables')
      .select('table_name')
      .eq('table_schema', 'public');
      
    if (tablesError) {
      console.error('🔍 DIAGNOSTICS ERROR: Failed to get tables list:', tablesError);
      results.tables.error = tablesError.message;
    } else {
      const hasDomainsTable = tables?.some((t: any) => t.table_name === 'approved_domains');
      results.tables.approved_domains_exists = hasDomainsTable;
      console.log('🔍 DIAGNOSTICS: approved_domains table exists:', hasDomainsTable);
      
      if (!hasDomainsTable) {
        return {
          success: false,
          message: 'The approved_domains table does not exist in the database',
          results
        };
      }
    }
    
    // 2. Check table structure
    console.log('🔍 DIAGNOSTICS: Checking table structure');
    const { data: columns, error: columnsError } = await supabase
      .from('information_schema.columns')
      .select('column_name, data_type, is_nullable')
      .eq('table_name', 'approved_domains')
      .eq('table_schema', 'public');
      
    if (columnsError) {
      console.error('🔍 DIAGNOSTICS ERROR: Failed to get columns:', columnsError);
      results.tables.columns_error = columnsError.message;
    } else {
      results.tables.columns = columns;
      
      // Check if required columns exist
      const requiredColumns = ['id', 'domain_pattern', 'campaign_id', 'is_active', 'created_at', 'updated_at'];
      const missingColumns = requiredColumns.filter(
        col => !columns?.some((c: { column_name: string }) => c.column_name === col)
      );
      
      results.tables.missing_columns = missingColumns;
      
      if (missingColumns.length > 0) {
        console.error('🔍 DIAGNOSTICS: Missing required columns:', missingColumns);
      } else {
        console.log('🔍 DIAGNOSTICS: All required columns exist');
      }
    }
    
    // 3. Check permissions
    console.log('🔍 DIAGNOSTICS: Checking user permissions');
    try {
      // Test SELECT permission
      const { error: selectError } = await supabase
        .from('approved_domains')
        .select('id')
        .limit(1);
        
      results.permissions.select = selectError ? false : true;
      
      if (selectError) {
        console.error('🔍 DIAGNOSTICS ERROR: No SELECT permission:', selectError);
      } else {
        console.log('🔍 DIAGNOSTICS: Has SELECT permission');
      }
      
      // Test INSERT permission with a test domain
      const testDomain = {
        domain_pattern: `test-${Date.now()}.example.com`,
        campaign_id: '00000000-0000-0000-0000-000000000000',
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      const { data: insertData, error: insertError } = await supabase
        .from('approved_domains')
        .insert(testDomain)
        .select();
        
      results.permissions.insert = insertError ? false : true;
      
      if (insertError) {
        console.error('🔍 DIAGNOSTICS ERROR: No INSERT permission:', insertError);
      } else {
        console.log('🔍 DIAGNOSTICS: Has INSERT permission, created test domain:', insertData);
        
        // If insert worked, test UPDATE
        if (insertData && insertData.length > 0) {
          const testId = insertData[0].id;
          results.test_operations.test_domain_id = testId;
          
          // Test UPDATE permission
          const { error: updateError } = await supabase
            .from('approved_domains')
            .update({ is_active: false })
            .eq('id', testId);
            
          results.permissions.update = updateError ? false : true;
          
          if (updateError) {
            console.error('🔍 DIAGNOSTICS ERROR: No UPDATE permission:', updateError);
          } else {
            console.log('🔍 DIAGNOSTICS: Has UPDATE permission');
          }
          
          // Test DELETE permission
          const { error: deleteError } = await supabase
            .from('approved_domains')
            .delete()
            .eq('id', testId);
            
          results.permissions.delete = deleteError ? false : true;
          
          if (deleteError) {
            console.error('🔍 DIAGNOSTICS ERROR: No DELETE permission:', deleteError);
            
            // Try alternative delete methods if standard fails
            console.log('🔍 DIAGNOSTICS: Trying alternative delete methods');
            
            try {
              await deleteDomain(testId);
              results.permissions.delete_alternative = true;
              console.log('🔍 DIAGNOSTICS: Alternative delete method succeeded');
            } catch (altDeleteError) {
              results.permissions.delete_alternative = false;
              console.error('🔍 DIAGNOSTICS ERROR: All delete methods failed:', altDeleteError);
            }
          } else {
            console.log('🔍 DIAGNOSTICS: Has DELETE permission');
          }
        }
      }
    } catch (permissionError) {
      console.error('🔍 DIAGNOSTICS ERROR: Error checking permissions:', permissionError);
      results.permissions.error = permissionError instanceof Error ? permissionError.message : 'Unknown error';
    }
    
    // 4. Check database policy
    try {
      console.log('🔍 DIAGNOSTICS: Checking RLS policies');
      const { data: policies, error: policiesError } = await supabase
        .from('pg_policies')
        .select('*')
        .ilike('tablename', 'approved_domains');
        
      if (policiesError) {
        console.error('🔍 DIAGNOSTICS ERROR: Failed to get policies:', policiesError);
        results.policies = { error: policiesError.message };
      } else {
        results.policies = policies;
        console.log('🔍 DIAGNOSTICS: Found policies:', policies);
      }
    } catch (policyError) {
      console.error('🔍 DIAGNOSTICS ERROR: Error checking policies:', policyError);
      results.policies = { error: policyError instanceof Error ? policyError.message : 'Unknown error' };
    }
    
    // 5. Summary
    const hasErrors = Object.values(results.permissions).includes(false);
    
    return {
      success: !hasErrors,
      message: hasErrors 
        ? 'Database diagnosis complete with permission issues detected' 
        : 'Database diagnosis complete, all checks passed',
      results
    };
  } catch (error) {
    console.error('🔍 DIAGNOSTICS ERROR: Unexpected error during diagnosis:', error);
    return {
      success: false,
      message: 'Database diagnosis failed with an unexpected error',
      error: error instanceof Error ? error.message : 'Unknown error',
      results
    };
  }
};

// Export a debug function for developers to use in the console
export const debugSupabase = {
  // Test direct deletion using different methods
  testDeleteDomain: async (domainId: string) => {
    console.log('🧪 TESTING DELETE: Starting test deletion for domain ID:', domainId);
    
    // Method 1: Direct delete from table
    try {
      console.log('🧪 TESTING METHOD 1: Standard delete');
      const { data: checkData, error: checkError } = await supabase
        .from('approved_domains')
        .select('*')
        .eq('id', domainId)
        .single();
        
      if (checkError) {
        console.error('🧪 SELECT CHECK FAILED:', checkError);
        throw new Error(`Cannot check domain: ${checkError.message}`);
      }
      
      if (!checkData) {
        console.error('🧪 DOMAIN NOT FOUND:', domainId);
        throw new Error('Domain not found with this ID');
      }
      
      console.log('🧪 FOUND DOMAIN TO DELETE:', checkData);
      
      // Try a direct delete
      const { data: deleteData, error: deleteError } = await supabase
        .from('approved_domains')
        .delete()
        .eq('id', domainId)
        .select();
        
      if (deleteError) {
        console.error('🧪 METHOD 1 FAILED:', deleteError);
      } else {
        console.log('🧪 METHOD 1 SUCCEEDED:', deleteData);
        return { method: 1, success: true, data: deleteData };
      }
    } catch (error) {
      console.error('🧪 METHOD 1 EXCEPTION:', error);
    }
    
    // Method 2: RPC Call
    try {
      console.log('🧪 TESTING METHOD 2: RPC delete_domain function');
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('delete_domain', { domain_id: domainId });
        
      if (rpcError) {
        console.error('🧪 METHOD 2 FAILED:', rpcError);
      } else {
        console.log('🧪 METHOD 2 SUCCEEDED:', rpcData);
        return { method: 2, success: true, data: rpcData };
      }
    } catch (error) {
      console.error('🧪 METHOD 2 EXCEPTION:', error);
    }
    
    // Method 3: Raw SQL (if available)
    try {
      console.log('🧪 TESTING METHOD 3: Raw SQL delete');
      const { data: sqlData, error: sqlError } = await supabase
        .rpc('execute_sql', {
          query_text: `DELETE FROM approved_domains WHERE id = '${domainId}' RETURNING *`
        });
        
      if (sqlError) {
        console.error('🧪 METHOD 3 FAILED:', sqlError);
      } else {
        console.log('🧪 METHOD 3 SUCCEEDED:', sqlData);
        return { method: 3, success: true, data: sqlData };
      }
    } catch (error) {
      console.error('🧪 METHOD 3 EXCEPTION:', error);
    }
    
    // Method 4: Auth bypass approach (if auth issue is suspected)
    try {
      console.log('🧪 TESTING METHOD 4: Auth bypass approach (if supported)');
      const { data: serviceRoleData, error: serviceRoleError } = await supabase.auth.getSession();
      
      console.log('🧪 Current auth session:', serviceRoleData);
      
      // Note: This might not work, depends on how service_role is implemented
      // This is just to collect information for debugging
      console.error('🧪 METHOD 4 NOT IMPLEMENTED');
    } catch (error) {
      console.error('🧪 METHOD 4 EXCEPTION:', error);
    }
    
    console.error('🧪 ALL DELETE METHODS FAILED');
    return { success: false, message: 'All delete methods failed' };
  },
  
  // Show current authentication state
  getAuthState: async () => {
    const { data, error } = await supabase.auth.getSession();
    return { session: data.session, error };
  },
  
  // Get database URL
  getDbInfo: () => {
    return {
      url: process.env.VITE_SUPABASE_URL || 'not-available',
      // Don't log the anon key in production
      anon_key_prefix: supabaseAnonKey.substring(0, 5) + '...',
      // Safe info
      auth_configured: !!supabase.auth
    };
  }
};

// Role helpers
export const getAppRoles = async () => {
  const { data, error } = await supabase.rpc('list_app_roles');
  if (error) throw error;
  return data as Array<{ role: string; label: string; description: string | null }>;
};

export const setUserRole = async (userId: string, role: string) => {
  const { error } = await supabase.rpc('set_user_role', { p_user_id: userId, p_role: role });
  if (error) throw error;
  return true;
};

export const setUserActive = async (userId: string, isActive: boolean) => {
  const { error } = await supabase.rpc('set_user_active', { p_user_id: userId, p_active: isActive });
  if (error) throw error;
  return true;
};

/**
 * Invites a teammate. Creating an auth user needs the service role, which the
 * browser must never hold, so this goes through the team-invite edge function.
 */
export const inviteTeamMember = async (email: string) => {
  const { data, error } = await supabase.functions.invoke('team-invite', {
    body: { email },
  });

  if (error) {
    // The function returns a useful message in the body; surface that rather
    // than the generic "non-2xx status code" the client throws.
    let detail = '';
    try {
      detail = (await (error as any).context?.json())?.error ?? '';
    } catch {
      detail = '';
    }
    throw new Error(detail || error.message || 'The invite could not be sent.');
  }

  if (data?.error) throw new Error(data.error);
  return data as { invited: boolean; email: string; user_id: string | null };
};
